'use client'

import { type ReactNode, useEffect, useMemo, useRef } from 'react'
import {
  OptimizelyProvider,
  createInstance,
  createPollingProjectConfigManager,
  createStaticProjectConfigManager,
  createBatchEventProcessor,
  createLogger,
  getSendBeaconEventDispatcher,
  ERROR,
  type Client,
  type UserInfo,
} from '@optimizely/react-sdk'
import { useSiteContext } from '@dustin-web/microsite-components/src/hooks'
import { createOptimizelyUserAttributes } from '../utils/optimizely-user-attributes'
import { useAuth } from '@dustin-web/microsite-components/src/auth'
import {
  createOptimizelyClosingEventDispatcher,
  createOptimizelyEventDispatcher,
} from '../utils/optimizely-event-dispatcher'
import { useCookieState } from '@dustin-web/microsite-components/src/hooks/use-cookie-state'
import { serverSettings } from '@dustin-web/microsite-config'

type Props = {
  children: ReactNode
  datafile: string | undefined
}

export function FeatureFlagsProvider({ children, datafile }: Props) {
  const { anonymousUserIdentifier, site, punchOutTicket, isMobile, deviceType, userAgent } =
    useSiteContext()
  const { cookies } = useCookieState()
  const { userData } = useAuth()
  const previousClientRef = useRef<Client | null>(null)

  const hasAnalyticsConsent = cookies.consent.analytics === 'true'

  const optimizelyClientInstance = useMemo(
    () =>
      createInstance({
        projectConfigManager: datafile
          ? createStaticProjectConfigManager({ datafile })
          : createPollingProjectConfigManager({ sdkKey: serverSettings.optimizelySdkKey }),
        eventProcessor: createBatchEventProcessor({
          eventDispatcher: createOptimizelyEventDispatcher(hasAnalyticsConsent),
          closingEventDispatcher: createOptimizelyClosingEventDispatcher(
            hasAnalyticsConsent,
            getSendBeaconEventDispatcher()
          ),
          flushInterval: 1000,
          batchSize: 10,
        }),
        logger: createLogger({ level: ERROR }),
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [datafile, hasAnalyticsConsent]
  )

  useEffect(() => {
    const previousClient = previousClientRef.current

    previousClientRef.current = optimizelyClientInstance

    if (previousClient && previousClient !== optimizelyClientInstance) {
      previousClient.close()
    }

  }, [optimizelyClientInstance])

  const userContext: UserInfo = {
    id: anonymousUserIdentifier,
    attributes: createOptimizelyUserAttributes(
      site,
      punchOutTicket,
      isMobile,
      deviceType,
      userAgent,
      userData ?? {}
    ),
  }

  return (
    <OptimizelyProvider client={optimizelyClientInstance} user={userContext}>
      {children}
    </OptimizelyProvider>
  )
}

export default FeatureFlagsProvider
